import React, { useState, useEffect, useRef, useCallback } from 'react';
import HeroImageCarousel from './HeroImageCarousel.js';

const isPrerender =
  typeof navigator !== 'undefined' &&
  navigator.userAgent &&
  navigator.userAgent.indexOf('ReactSnap') !== -1;

const HeroVideo = ({ videoSrc, posterSrc, images = [], children }) => {
  const [useStaticFallback, setUseStaticFallback] = useState(isPrerender);
  const [videoEnded, setVideoEnded] = useState(false);
  const [videoFading, setVideoFading] = useState(false);
  const videoRef = useRef(null);
  const fadeTimeout = useRef(null);

  useEffect(() => {
    if (isPrerender) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) setUseStaticFallback(true);
  }, []);

  useEffect(() => {
    return () => { if (fadeTimeout.current) clearTimeout(fadeTimeout.current); };
  }, []);

  const handleVideoEnd = useCallback(() => {
    if (images.length === 0) {
      if (videoRef.current) videoRef.current.play();
      return;
    }
    setVideoFading(true);
    fadeTimeout.current = setTimeout(() => {
      setVideoEnded(true);
    }, 1500);
  }, [images.length]);

  const showVideo = videoSrc && !useStaticFallback && !videoEnded;
  const carouselImages = images.length > 0 ? images : (posterSrc ? [posterSrc] : []);

  return (
    <div className="hero relative flex flex-col justify-end overflow-hidden">
      {/* Carousel — only starts after video ends */}
      {videoEnded && carouselImages.length > 0 && (
        <HeroImageCarousel images={carouselImages} />
      )}

      {/* Video — visible immediately (browser shows first frame), plays when buffered */}
      {showVideo && (
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1500ms]"
          style={{ opacity: videoFading ? 0 : 1, zIndex: 1 }}
          aria-hidden="true"
          onEnded={handleVideoEnd}
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
      )}

      {/* Static fallback for prerender / reduced motion */}
      {useStaticFallback && posterSrc && (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${posterSrc})` }}
          aria-hidden="true"
        />
      )}

      <div className="absolute inset-0 bg-[#000] bg-opacity-50" style={{ zIndex: 2 }} />
      <div style={{ zIndex: 3, position: 'relative' }}>
        {children}
      </div>
    </div>
  );
};

export default HeroVideo;
